import { Crosshair, BarChart3, Palette, Layers, Database, MessageSquare, CheckCircle2 } from 'lucide-react';

const differentiators = [
  {
    icon: Crosshair,
    title: 'Strategy-First Execution',
    description: 'Every campaign starts with deep research and a custom playbook — not a template. Your strategy is built for YOUR market.',
  },
  {
    icon: BarChart3,
    title: 'Performance-Driven Decisions',
    description: 'We optimize based on revenue metrics, not vanity numbers. Every dollar in your budget is accountable to real business outcomes.',
  },
  {
    icon: Palette,
    title: 'Premium Creative Direction',
    description: 'Thumb-stopping creatives backed by data. Our in-house team produces ads that convert AND elevate your brand.',
  },
  {
    icon: Layers,
    title: 'Funnel + Ads Alignment',
    description: 'We architect the entire journey — from first click to closed deal — ensuring your funnel and ads work as a unified system.',
  },
  {
    icon: Database,
    title: 'Data-Backed Optimization',
    description: 'Advanced analytics, attribution modeling, and automated reporting give you full visibility into what\'s working and what\'s not.',
  },
  {
    icon: MessageSquare,
    title: 'Direct Senior Access',
    description: 'No account managers in between. You work directly with senior strategists who know your account inside and out.',
  },
];

const benefits = [
  'No long-term contracts required',
  'Weekly performance reporting',
  'Dedicated strategist on every account',
  'Transparent pricing, no hidden fees',
];

export default function WhyChooseUs() {
  return (
    <section id="why-us" data-testid="why-us-section" className="section-padding bg-vertex-gradient relative overflow-hidden">
      <div className="absolute inset-0 bg-vertex-radial" />
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-vertex-500/30 to-transparent" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-vertex-500/10 border border-vertex-500/20 mb-6">
            <span className="text-vertex-300 text-sm font-semibold">Why Vertex</span>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-display font-bold text-white mb-6 leading-tight">
            Not Another
            <br />
            <span className="gradient-text">Cookie-Cutter Agency</span>
          </h2>
          <p className="text-gray-400 text-lg leading-relaxed">
            Most agencies sell you a package. We build you a growth engine. Here's what makes working with Vertex fundamentally different.
          </p>
        </div>

        {/* Cards Grid - 3x2 */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-16">
          {differentiators.map((item) => (
            <div
              key={item.title}
              className="group glass rounded-2xl p-6 card-hover hover:border-vertex-500/30 cursor-default"
            >
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="p-3 rounded-xl bg-vertex-500/15 group-hover:bg-vertex-500/25 transition-colors duration-300">
                    <item.icon className="w-5 h-5 text-vertex-400" />
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-display font-bold text-white mb-2 group-hover:text-vertex-300 transition-colors duration-300">
                    {item.title}
                  </h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{item.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Benefits Bar */}
        <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3">
          {benefits.map((benefit) => (
            <div key={benefit} className="flex items-center gap-2.5">
              <CheckCircle2 className="w-4 h-4 text-vertex-400 flex-shrink-0" />
              <span className="text-gray-300 text-sm">{benefit}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
